/**
 * roleReportDefaults — role-tailored default selection for the Reports home.
 *
 * Phase 3 of the reports reorganization adds role-aware visibility on top of the
 * workspace-level report settings. Two layers:
 *
 *   Layer A — Capability floor (hard). Enforced in ReportsIndex via `can()`:
 *     a report shows only if the user holds its `requiredCapability`.
 *   Layer B — Role-tailored default (soft, this module). Each report-relevant
 *     role has a default set of home reports; the home shows that set by default
 *     with a "Show all" escape hatch.
 *
 * This mirrors the home-dashboard per-role pattern (lib/contributorNavProfiles.ts +
 * lib/composeHomeDashboard.ts): a static slug→config map, unioned across a user's
 * assigned roles. Defaults are code-defined and easily tuned — there is no
 * per-role admin editor in this phase.
 *
 * Roles here are the REAL assignable roles: the 5 ActorRoles (roles.ts) and the
 * job-role slugs (jobRoles.ts). Cycle-level authority roles (Cycle Owner, Ball
 * Holder, Decision Authority, Facilitator) are NOT assignable and are not modeled
 * — cycle-role gating was retired (canon 06 §13).
 *
 * React/Zustand-free so the selection logic is unit-testable in isolation.
 */

import { isFeatureEnabled, type FeatureFlagValue } from './featureFlags';

/** Report keys that exist as home cards (mirrors HOME_REPORTS keys in reportSettingsService.ts). */
export const HOME_REPORT_KEYS = [
  'pipeline-health',
  'session-reports',
  'cycle-execution',
  'cycle-reports',
  'relationship-health',
  'intake-clarity',
  'strategy-alignment',
  'decision-latency',
] as const;

export type HomeReportKey = (typeof HOME_REPORT_KEYS)[number];

/**
 * Per-role default home-report sets, keyed by job-role slug (jobRoles.ts).
 * A user who holds several of these gets the UNION. Roles not listed contribute
 * nothing; a user matching none falls back to BASELINE_REPORT_SET.
 */
export const ROLE_REPORT_DEFAULTS: Record<string, HomeReportKey[]> = {
  // Platform / analytics — see everything
  'reports-analyst': [...HOME_REPORT_KEYS],
  'plan-of-record-manager': [...HOME_REPORT_KEYS],

  // Strategy
  strategist: ['strategy-alignment', 'pipeline-health', 'relationship-health', 'cycle-reports'],

  // Sessions
  'session-participant': ['session-reports'],
  'session-facilitator': ['session-reports'],
  'session-designer': ['session-reports'],
  'tool-selection-authority': ['session-reports'],

  // Cycles / governance
  'cycle-designer': ['cycle-execution', 'cycle-reports', 'session-reports'],
  'local-cycle-designer': ['cycle-execution', 'cycle-reports', 'session-reports'],
  'track-owner': ['cycle-execution', 'cycle-reports', 'session-reports'],
  'priority-list-owner': ['cycle-execution', 'cycle-reports', 'session-reports'],
  'ball-holder': ['cycle-execution', 'cycle-reports', 'session-reports'],
  'catchball-contributor': ['cycle-execution', 'cycle-reports', 'session-reports'],
  'priority-setter': ['cycle-execution', 'cycle-reports', 'session-reports'],
  'priority-approver': ['cycle-execution', 'cycle-reports', 'session-reports'],
  'decision-authority': ['cycle-execution', 'cycle-reports', 'session-reports', 'decision-latency'],
  'selection-authority': ['cycle-execution', 'cycle-reports', 'session-reports'],

  // Backlog / items
  'item-manager': ['pipeline-health', 'intake-clarity', 'relationship-health'],
  'item-triage': ['pipeline-health', 'intake-clarity', 'relationship-health'],
  'idea-submitter': ['pipeline-health', 'intake-clarity'],
  'relationship-curator': ['pipeline-health', 'relationship-health'],

  // Measurement / criteria
  'criteria-authority': ['pipeline-health', 'cycle-reports'],
  'item-selection-authority': ['pipeline-health', 'cycle-reports'],
};

/** Fallback for a user who holds no role in ROLE_REPORT_DEFAULTS (e.g. a plain workspace member). */
export const BASELINE_REPORT_SET: HomeReportKey[] = ['pipeline-health', 'session-reports'];

/**
 * Resolve the role-tailored default set of home-report keys for a user.
 *
 * - Admins (isAdmin) see every home report.
 * - Otherwise the union of each assigned role's default set.
 * - No matched role → BASELINE_REPORT_SET.
 *
 * `assignedRoleNames` are the raw role-name strings from userStore (job-role
 * slugs and/or admin role names).
 */
export function resolveRoleReportDefaults(
  assignedRoleNames: string[],
  isAdmin: boolean
): Set<string> {
  if (isAdmin) return new Set(HOME_REPORT_KEYS);

  const acc = new Set<string>();
  for (const name of assignedRoleNames) {
    const set = ROLE_REPORT_DEFAULTS[name];
    if (set) for (const key of set) acc.add(key);
  }
  if (acc.size === 0) for (const key of BASELINE_REPORT_SET) acc.add(key);
  return acc;
}

// ============================================================================
// Home report selection (Layer A floor + workspace visibility + Layer B role)
// ============================================================================

export interface HomeReportLike {
  key: string;
  /** Capability required to view (Layer A). Undefined = no capability gate. */
  requiredCapability?: string;
  /** Workspace module the report lives behind (§2.7). Undefined = always on. */
  moduleKey?: string;
  /** A feature switch the report's page also needs (as the sidebar checks it). Undefined = none. */
  featureFlag?: string;
}

export interface SelectHomeReportsParams<T extends HomeReportLike> {
  reports: T[];
  /** Capability check (typically userStore.can). */
  canView: (capability: string) => boolean;
  /** Whether the user's permissions have loaded. Floor is skipped (fail-open) while false. */
  permissionsLoaded: boolean;
  /** Role-tailored default key set (Layer B). */
  roleDefaults: Set<string>;
  /** When true, show every report that passes the floor + workspace visibility (the "Show all" escape hatch). */
  showAll: boolean;
  /** Workspace visibility toggle (reportSettingsStore.getSetting(key).isVisible). */
  isVisible: (key: string) => boolean;
  /** Workspace required/pinned toggle. Required reports always show. */
  isRequired: (key: string) => boolean;
  /**
   * The canonical module gate (resolveModuleGate). A report with a moduleKey
   * shows only when this says the module is on; without it, such a report is
   * hidden — a card that opens a switched-off page is a control the person
   * cannot use (§2.8).
   */
  isModuleOn?: (moduleKey: string) => boolean;
}

/**
 * Pure selection of which home reports to render.
 *
 * Order of gates:
 *   1. Workspace visibility — hidden reports never show (unless required).
 *   2. Capability floor (Layer A) — hide when permissions are loaded AND the user
 *      lacks the report's requiredCapability. Fail-open while permissions load so
 *      the home never blanks.
 *   3. Role/show-all (Layer B) — when not showAll, keep only role-default reports;
 *      required reports always pass regardless of role or showAll.
 */
export function selectHomeReports<T extends HomeReportLike>(
  params: SelectHomeReportsParams<T>
): T[] {
  const { reports, canView, permissionsLoaded, roleDefaults, showAll, isVisible, isRequired, isModuleOn } = params;

  return reports.filter((r) => {
    // 0. Module gate — a report behind a switched-off module never shows.
    if (r.moduleKey && !(isModuleOn?.(r.moduleKey) ?? false)) return false;
    // 0b. Feature switch — the same one the sidebar checks for the page.
    if (r.featureFlag && !isFeatureEnabled(r.featureFlag as FeatureFlagValue)) return false;

    const required = isRequired(r.key);

    // 1. Workspace visibility (required reports are always visible by construction).
    if (!required && !isVisible(r.key)) return false;

    // 2. Capability floor — only enforced once permissions are loaded (fail-open otherwise).
    //    Required reports still honor the floor: a report the user cannot access is not
    //    surfaced just because an admin pinned it.
    if (permissionsLoaded && r.requiredCapability && !canView(r.requiredCapability)) {
      return false;
    }

    // 3. Role default / show-all. Required reports always pass.
    if (required || showAll) return true;
    return roleDefaults.has(r.key);
  });
}
